import { ExpandLess } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Button,
  ClickAwayListener,
  Collapse,
  Grid,
  Paper,
} from "@mui/material";
import { CustomStatusBadge } from "components/StatusBadge";
import EditFormControl from "components/form/EditFormControl";
import CustomText from "components/typography/CustomText";
import { formatArray } from "lib/arrayObject";
import { getWeekDaysByWeekNumber, weekCount } from "lib/date";
import { currency } from "lib/formatter";
import { boxStatusOptions, postcodes } from "lib/global";
import { isEmpty, toNumber } from "lodash";
import { checkSubLog, updateBox } from "models/boxesModel";
import frequencyOptions from "models/frequencyOptions.json";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import BoxEditForm from "./pie/BoxEditForm";
import PieGrid from "./pie/PieGrid";

export default function SubscriptionSummaryInfo({
  data = {},
  onUpdate = () => {},
}) {
  const formRef = useRef();
  const [open, setOpen] = useState(false);
  const [boxInfo, setBoxInfo] = useState({});
  const [boxFormData, setBoxFormData] = useState({});
  const [weeks, setWeeks] = useState([]);
  const [weekDays, setWeekDays] = useState([
    { id: "1", label: "Monday" },
    { id: "2", label: "Tuesday" },
    { id: "3", label: "Wednesday" },
    { id: "4", label: "Thursday" },
  ]);
  const auth = useSelector((state) => state?.auth?.user ?? {});

  const handleUpdate = () => {
    const submitData = formRef.current.prepare();
    const week_num = data?.week_next_num || data?.week_num;
    const next_week_num =
      weeks.filter((item) => item?.value === week_num)?.[0] ?? {};
    let log = checkSubLog({
      newValue: boxFormData,
      initData: { ...data, week_next_num: next_week_num },
      changerName: auth?.name,
    });
    const log_data = {
      user_id: boxFormData?.user_id ?? "",
      changer_id: auth?.id,
      changer_type: "admin",
      box_id: boxFormData?.id,
      log: log,
    };
    if (submitData) {
      updateBox({
        data: { ...boxFormData, log_data: log_data },
        onFinish: (res, err) => {
          if (res === false) setBoxFormData((s = {}) => boxInfo);
          onUpdate(res, err);
          setOpen(false);
        },
      });
    }
  };

  const setCurrentYearWeeks = () => {
    const weeklyInfos = Array.from(Array(weekCount).keys()).reduce(
      (ret, cur, index) => {
        const label = `Week ${cur + 1}`;
        return [...ret, { value: cur + 1, label: label }];
      },
      []
    );
    setWeeks(() => weeklyInfos);
  };

  const calcWeekDays = () => {
    if (
      postcodes.some((postcode) => postcode === boxFormData?.address?.postcode)
    ) {
      setWeekDays((prev) => [...prev, { id: "5", label: "Friday" }]);
    }
  };

  useEffect(
    () => calcWeekDays(),
    //eslint-disable-next-line
    [boxFormData]
  );

  useEffect(() => {
    if (!isEmpty(data)) {
      const week_num = data?.week_next_num || data?.week_num;
      const next_week_num =
        weeks.filter((item) => item?.value === week_num)?.[0] ?? {};
      setBoxInfo({
        ...data,
        status: !Boolean(data?.status)
          ? boxStatusOptions?.[0]
          : boxStatusOptions?.filter(
              (item) => item?.value === data?.status
            )?.[0] ?? {},
        week_next_num: next_week_num,
        frequency:
          frequencyOptions?.filter(
            (item) => item?.name === data?.frequency
          )?.[0] ?? null,
      });
    }
  }, [data, weeks]);

  useEffect(() => {
    setCurrentYearWeeks();
  }, []);

  useEffect(() => setBoxFormData(boxInfo), [boxInfo]);

  return (
    <>
      <Paper sx={{ backdropFilter: "2px" }} className="relative">
        <div
          className="flex justify-between w-full hover:cursor-pointer border-b border-white-gray px-4"
          onClick={() => setOpen((prev) => !prev)}
        >
          <div className="flex flex-1 py-3">
            <div className="w-40 md:w-56 py-1">
              <CustomText font="mont" variant="body1">
                Current Subscription
              </CustomText>
              <CustomStatusBadge
                size="body"
                variant={boxInfo?.status?.value === "1" ? "success" : "error"}
                text={boxInfo?.status?.label}
              />
            </div>
            <div className="flex flex-wrap flex-1">
              <div className="px-2 md:px-4 py-1">
                <CustomText font="mont" variant="body1">
                  VALUE
                </CustomText>
                <CustomText variant="body2">
                  {currency.format(
                    formatArray(data?.slots).reduce(
                      (ret, cur) => ret + toNumber(cur?.amount),
                      0
                    )
                  )}
                </CustomText>
              </div>
              <div className="px-2 md:px-4 py-1">
                <CustomText variant="body1">FREQUENCY</CustomText>
                <CustomText variant="body2">
                  {boxInfo?.frequency?.name ?? ""}
                </CustomText>
              </div>
              <div className="px-2 md:px-4 py-1">
                <CustomText variant="body1">NEXT CHARGE</CustomText>
                <CustomText variant="body2">
                  {getWeekDaysByWeekNumber({
                    weekNumber: boxInfo?.week_next_num?.value - 2,
                    addDay: 6,
                    yearNumber: boxInfo?.week_year,
                  })}
                </CustomText>
              </div>
              <div className="px-2 md:px-4 py-1">
                <CustomText variant="body1">NEXT DISPATCH</CustomText>
                <CustomText variant="body2">
                  {boxInfo?.week_next_num?.label ?? ""}
                </CustomText>
              </div>
              <div className="px-2 md:px-4 py-1">
                <CustomText variant="body1">DELIVERY REGION</CustomText>
                <CustomText variant="body2">
                  {boxInfo?.address?.label ?? ""}
                </CustomText>
              </div>
            </div>
          </div>
          <div className="border-l border-white-gray flex items-center p-3">
            <CustomText variant="body2">Edit</CustomText>
            {open ? <ExpandLess /> : <ExpandMoreIcon />}
          </div>
        </div>
        <Collapse in={open} unmountOnExit className="box-detail">
          <ClickAwayListener onClickAway={() => console.log}>
            <Paper
              className={`absolute top-0 left-0 w-full z-10 ${
                boxInfo?.status?.value === "0" ? "bg-white-gray" : ""
              }`}
              elevation={4}
            >
              <div
                className={`flex justify-between w-full hover:cursor-pointer border-b px-4 ${
                  boxInfo?.status?.value === "0"
                    ? "border-white"
                    : "border-white-gray "
                }`}
                onClick={() => setOpen((prev) => !prev)}
              >
                <div className="flex flex-1 py-3">
                  <div className="w-40 md:w-56 py-1">
                    <CustomText font="mont" variant="body1">
                      Current Subscription
                    </CustomText>{" "}
                    <CustomStatusBadge
                      size="body"
                      variant={
                        boxInfo?.status?.value === "1" ? "success" : "error"
                      }
                      text={boxInfo?.status?.label}
                    />
                  </div>
                  <div className="flex flex-wrap flex-1">
                    <div className="px-2 md:px-4 py-1">
                      <CustomText font="mont" variant="body1">
                        VALUE
                      </CustomText>
                      <CustomText variant="body2">
                        {currency.format(
                          formatArray(data?.slots).reduce(
                            (ret, cur) => ret + toNumber(cur?.amount),
                            0
                          )
                        )}
                      </CustomText>
                    </div>
                    <div className="px-2 md:px-4 py-1">
                      <CustomText variant="body1">FREQUENCY</CustomText>
                      <CustomText variant="body2">
                        {boxInfo?.frequency?.name ?? ""}
                      </CustomText>
                    </div>
                    <div className="px-2 md:px-4 py-1">
                      <CustomText variant="body1">NEXT CHARGE</CustomText>
                      <CustomText variant="body2">
                        {getWeekDaysByWeekNumber({
                          weekNumber: boxInfo?.week_next_num?.value - 2,
                          addDay: 6,
                          yearNumber: boxInfo?.week_year,
                        })}
                      </CustomText>
                    </div>
                    <div className="px-2 md:px-4 py-1">
                      <CustomText variant="body1">NEXT SLOT</CustomText>
                      <CustomText variant="body2">
                        {boxInfo?.week_next_num?.label}
                      </CustomText>
                    </div>
                    <div className="px-2 md:px-4 py-1">
                      <CustomText variant="body1">DELIVERY REGION</CustomText>
                      <CustomText variant="body2">
                        {boxInfo?.address?.label ?? ""}
                      </CustomText>
                    </div>
                  </div>
                </div>
                <div className="border-l border-white-gray flex items-center p-3">
                  <CustomText variant="body2">Edit</CustomText>
                  {open ? <ExpandLess /> : <ExpandMoreIcon />}
                </div>
              </div>
              <div
                className={`border-b px-4 ${
                  boxInfo?.status?.value === "0"
                    ? "border-white"
                    : "border-white-gray "
                }`}
              >
                <CustomText className="py-4" variant="h6" bold={600}>
                  Flavors
                </CustomText>
              </div>
              <div
                className={`p-4 ${
                  boxInfo?.status?.value === "0" ? "pointer-events-none" : ""
                }`}
              >
                <PieGrid
                  data={boxInfo?.pies ?? []}
                  onChange={(v) => setBoxInfo((s = {}) => ({ ...s, pies: v }))}
                />
              </div>
              <div
                className={`border-b border-t px-4 ${
                  boxInfo?.status?.value === "0"
                    ? "border-white"
                    : "border-white-gray "
                }`}
              >
                <CustomText className="py-4" variant="h6" bold={600}>
                  Details
                </CustomText>
              </div>
              <div
                className={`p-4 ${
                  boxInfo?.status?.value === "0" ? "pointer-events-none" : ""
                }`}
              >
                <BoxEditForm
                  data={boxFormData}
                  onChange={setBoxFormData}
                  ref={formRef}
                  weeks={weeks}
                />
                <Grid className="py-5" container spacing={2} rowSpacing={2}>
                  <Grid item sm={12} md={2}>
                    <EditFormControl
                      label="Dispatch Same Day"
                      type="checkbox"
                      value={boxFormData?.same_week ?? false}
                      onChange={(e) =>
                        setBoxFormData((prev) => ({
                          ...prev,
                          same_week: e.target.value,
                        }))
                      }
                    />
                  </Grid>
                  {boxFormData?.same_week && (
                    <Grid item sm={12} md={2}>
                      <EditFormControl
                        label="Week Day"
                        type="native_select"
                        options={weekDays}
                        fullWidth
                        value={boxFormData?.week_day ?? null}
                        onChange={(e) =>
                          setBoxFormData((prev) => ({
                            ...prev,
                            week_day: e.target.value,
                          }))
                        }
                      />
                    </Grid>
                  )}
                </Grid>
                <div className="z-20">
                  <div className="flex flex-start py-4">
                    <Button
                      className="pointer-events-auto"
                      color="secondary"
                      variant="contained"
                      onClick={handleUpdate}
                    >
                      Save Changes
                    </Button>
                    <Button
                      className="bg-light-gray ml-2 pointer-events-auto"
                      variant="contained"
                      onClick={() => setOpen(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
              {/* {Boolean(boxInfo?.status?.value === "0") && (
                <div className="w-full h-full bg-white-gray opacity-50 absolute top-0 left-0"></div>
              )} */}
            </Paper>
          </ClickAwayListener>
        </Collapse>
      </Paper>
    </>
  );
}
